/**
 * Parsing for the `reason` string the engine stamps on every changeover event.
 *
 * Lives in its own module rather than in `GenerateChangeoverRows` because FOUR
 * surfaces describe the same event — the Changeover grid, the Line grid, the
 * Kitchen grid and the factory Gantt — and `GenerateChangeoverRows` already
 * imports from `GenerateKitchenRows` (`HOURS_IN_A_WEEK`), so having the row
 * generators import back the other way would close a cycle the codebase
 * deliberately avoids (see the note above `CHANGEOVER` in
 * `GenerateKitchenRows.ts`).
 *
 * Wire format is produced by `changeover_events_to_json` in
 * `scheduler_rust/src/postprocess.rs`; the reason text itself is built at the
 * push sites in `simplified_decoder.rs`.
 */
import { ChangeoverDetail } from './SchedulerView.types'

// Re-exported so a consumer needs one import for the whole changeover-display
// vocabulary rather than two that must be kept in step.
export type { ChangeoverDetail }

/** One changeover event as it arrives on the wire. */
export type ChangeoverWireEvent = {
  changeover_asset?: string
  asset_id?: string
  start: number
  end: number
  reason?: string
  cost?: number
  /** Resource the change HOLDS for its whole duration, and how much of it.
   *  Only a bottle-format change sets these today. Stamped on the event by
   *  the engine rather than looked up here, so a stored schedule keeps
   *  reporting the draw it actually ran with. */
  resource?: string
  resource_amount?: number
}

/** Parse an `additional_*_changeovers` column, which arrives as a JSON string
 *  (or, on some paths, already parsed). Never throws — a malformed value is
 *  simply no events. */
export const parseChangeoverEvents = (
  raw: unknown
): ChangeoverWireEvent[] => {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(parsed) ? (parsed as ChangeoverWireEvent[]) : []
  } catch {
    return []
  }
}

// A bottle-format change reports itself as
//   format_change:FROM->TO (RESOURCE)
// with the trailing ` (RESOURCE)` half present only when the change holds a
// resource while it runs. Parsed rather than re-derived because the Portal has
// no format table of its own to look the pair up in. Split by hand rather than
// by regex so a format name containing a bracket still comes apart at the
// right place — the resource suffix is taken from the LAST bracket group, the
// arrow from the FIRST one.
const FORMAT_PREFIX = 'format_change:'

/** The from/to/held-resource a `reason` carries, or an empty object for any
 *  changeover that is not a bottle-format change. */
export type ChangeoverReasonParts = {
  from?: string
  to?: string
  heldResource?: string
}

export const parseChangeoverReason = (
  raw: string | undefined
): ChangeoverReasonParts => {
  if (!raw || !raw.startsWith(FORMAT_PREFIX)) return {}
  const parts: ChangeoverReasonParts = {}
  let body = raw.slice(FORMAT_PREFIX.length)
  if (body.endsWith(')')) {
    const open = body.lastIndexOf(' (')
    if (open !== -1) {
      parts.heldResource = body.slice(open + 2, -1).trim() || undefined
      body = body.slice(0, open)
    }
  }
  const arrow = body.indexOf('->')
  if (arrow !== -1) {
    parts.from = body.slice(0, arrow).trim() || undefined
    parts.to = body.slice(arrow + 2).trim() || undefined
  }
  return parts
}

/** What the change IS, in one phrase: the format transition where there is
 *  one, otherwise the engine's own reason text (`equipment_switch`,
 *  `product_change`, …) tidied for display. Empty string when the event
 *  carries no reason at all, so callers can skip the row entirely. */
export const changeoverWhat = (raw: string | undefined): string => {
  const { from, to } = parseChangeoverReason(raw)
  if (from || to) return `${from ?? '(none)'} to ${to ?? '(none)'}`
  return (raw ?? '').replace(/_/g, ' ')
}

/** Describe one wire event for display against the resource it happens on. */
export const describeChangeoverEvent = (
  evt: ChangeoverWireEvent,
  resource: string
): ChangeoverDetail => ({
  resource,
  raw: evt.reason ?? '',
  cost: evt.cost,
  start: evt.start,
  end: evt.end,
  ...parseChangeoverReason(evt.reason),
})

/** Append `detail` unless an identical event is already recorded. One event
 *  can reach a cell from several schedule rows for the same resource, and the
 *  scalar primary event can duplicate its own entry in the JSON array. */
export const pushChangeoverDetail = (
  list: ChangeoverDetail[],
  detail: ChangeoverDetail
): void => {
  const dup = list.some(
    (d) =>
      d.start === detail.start &&
      d.end === detail.end &&
      d.raw === detail.raw &&
      d.resource === detail.resource
  )
  if (!dup) list.push(detail)
}
